export type OpportunityType =
  | "similar_request_consolidation"
  | "multi_location_consolidation"
  | "volume_negotiation"
  | "compliant_alternative_supplier"
  | "early_payment_discount"
  | "payment_term_improvement"
  | "renewal_renegotiation"
  | "asset_reuse"
  | "inventory_reallocation"
  | "catalogue_candidate"
  | "rate_contract_candidate"
  | "planned_emergency_purchase"
  | "approval_process_improvement"
  | "policy_safe_acceleration"
  | "early_sourcing"
  | "supplier_concentration"
  | "avoided_repurchase"
  | "budget_reallocation"
  | "working_capital";

export type ValueState = "potential" | "in_progress" | "realised" | "finance_verified";

export const opportunityTypes: ReadonlyArray<{
  type: OpportunityType;
  label: string;
  requiredEvidence: readonly string[];
  enabled: boolean;
  dependency?: string;
}> = [
  { type: "similar_request_consolidation", label: "Similar requests that can be consolidated", requiredEvidence: ["request line specification", "required date", "location or department"], enabled: false, dependency: "Verified semantic item/specification matching and approved planning window" },
  { type: "multi_location_consolidation", label: "Multi-location or multi-department demand consolidation", requiredEvidence: ["location or department", "compatible requirement", "required date"], enabled: false, dependency: "Verified cross-location demand normalisation" },
  { type: "volume_negotiation", label: "Volume negotiation opportunity", requiredEvidence: ["comparable demand", "current commercial baseline", "sourcing window"], enabled: false, dependency: "Comparable item normalisation and approved commercial baseline" },
  { type: "compliant_alternative_supplier", label: "Better-performing compliant alternative supplier", requiredEvidence: ["supplier compliance", "comparable specification", "performance history"], enabled: false, dependency: "Sufficient comparable supplier performance population" },
  { type: "early_payment_discount", label: "Early-payment discount opportunity", requiredEvidence: ["contracted discount", "approved invoice", "cash availability"], enabled: false, dependency: "Explicit supplier discount terms and finance-authorised cash context" },
  { type: "payment_term_improvement", label: "Payment-term improvement opportunity", requiredEvidence: ["current terms", "supplier agreement", "payment history"], enabled: false, dependency: "Normalised contract terms and finance-reviewed methodology" },
  { type: "renewal_renegotiation", label: "Upcoming renewal that can be renegotiated", requiredEvidence: ["contract end or notice date", "owner", "current commercial basis"], enabled: false, dependency: "Complete contract notice dates and accountable owners" },
  { type: "asset_reuse", label: "Asset available for authorised reuse review", requiredEvidence: ["compatible specification", "condition", "availability", "location", "custodian", "transfer basis"], enabled: false, dependency: "Structured specification compatibility, condition evidence, transfer cost and reuse restrictions" },
  { type: "inventory_reallocation", label: "Excess inventory that can be reallocated", requiredEvidence: ["available quantity", "compatible requirement", "location", "expiry or restriction"], enabled: false, dependency: "Reliable multi-location availability and compatibility data" },
  { type: "catalogue_candidate", label: "Recurring purchase suitable for a catalogue", requiredEvidence: ["repeat demand", "stable specification", "approved supplier basis"], enabled: false, dependency: "Repeat-pattern validation and catalogue governance" },
  { type: "rate_contract_candidate", label: "Recurring purchase suitable for a rate contract or blanket PO", requiredEvidence: ["repeat demand", "commercial baseline", "forecast period"], enabled: false, dependency: "Verified recurrence and contract-authority workflow" },
  { type: "planned_emergency_purchase", label: "Repeated emergency purchase that can be planned", requiredEvidence: ["emergency classifications", "repeat pattern", "lead time"], enabled: false, dependency: "Reliable emergency-reason coding and planning horizon" },
  { type: "approval_process_improvement", label: "Process change that could reduce approval time", requiredEvidence: ["workflow timestamps", "route", "outcome"], enabled: false, dependency: "Representative completed-cycle history" },
  { type: "policy_safe_acceleration", label: "Approval route that may qualify for policy-safe acceleration", requiredEvidence: ["server policy evaluation", "risk state", "route history"], enabled: false, dependency: "Machine 3B server-authorised fast-lane result" },
  { type: "early_sourcing", label: "Requirement that should enter sourcing earlier", requiredEvidence: ["required date", "lead time", "current stage"], enabled: false, dependency: "Reliable lead-time history and planning dates" },
  { type: "supplier_concentration", label: "Supplier concentration reduction opportunity", requiredEvidence: ["category spend", "supplier share", "qualified alternatives"], enabled: false, dependency: "Complete category allocation and qualified-alternative evidence" },
  { type: "avoided_repurchase", label: "Avoided repurchase", requiredEvidence: ["replacement request", "reused asset or reallocated stock", "approved baseline"], enabled: false, dependency: "Completed reuse/reallocation outcome and approved replacement baseline" },
  { type: "budget_reallocation", label: "Budget reallocation opportunity", requiredEvidence: ["approved budget", "commitments", "authorised forecast"], enabled: false, dependency: "Finance-authorised forecast and reallocation workflow" },
  { type: "working_capital", label: "Working-capital improvement opportunity", requiredEvidence: ["commitments", "payment terms", "approved invoice timing"], enabled: false, dependency: "Finance-reviewed cash timing methodology" },
] as const;

export type ProcurementPotentialInput = {
  annualSpend: number;
  recurringPercent: number;
  emergencyPercent: number;
  monthlyRequests: number;
  approvalDays: number;
  competitiveQuotePercent: number;
  openCommitmentVisibilityPercent: number;
  assetVisibilityPercent: number;
  auditPreparationDays: number;
  userAssumptionLowPercent: number;
  userAssumptionHighPercent: number;
};

export type ProcurementPotentialResult = {
  addressableSpend: number;
  improvementLow: number;
  improvementHigh: number;
  consolidationReviewSpend: number;
  negotiationReviewSpend: number;
  approvalDaysRecoverableRange: [number, number];
  commitmentVisibilityGap: number;
  auditReadinessOpportunity: string;
  confidence: "Low" | "Medium" | "High";
  methodology: readonly string[];
  assumptions: readonly string[];
  actions: readonly string[];
};

const between = (value: number, minimum: number, maximum: number) =>
  Math.min(maximum, Math.max(minimum, Number.isFinite(value) ? value : minimum));

export function calculateProcurementPotential(raw: ProcurementPotentialInput): ProcurementPotentialResult {
  const annualSpend = Math.max(0, raw.annualSpend);
  const recurring = between(raw.recurringPercent, 0, 100) / 100;
  const emergency = between(raw.emergencyPercent, 0, 100) / 100;
  const quoteGap = 1 - between(raw.competitiveQuotePercent, 0, 100) / 100;
  const commitmentGap = 1 - between(raw.openCommitmentVisibilityPercent, 0, 100) / 100;
  const lowRate = between(raw.userAssumptionLowPercent, 0, 100) / 100;
  const highRate = Math.max(lowRate, between(raw.userAssumptionHighPercent, 0, 100) / 100);
  const consolidationReviewSpend = annualSpend * recurring;
  const negotiationReviewSpend = annualSpend * quoteGap;
  const addressableSpend = Math.min(annualSpend, consolidationReviewSpend + negotiationReviewSpend * 0.5 + annualSpend * emergency * 0.25);
  const confidencePoints = [
    annualSpend > 0,
    raw.monthlyRequests > 0,
    raw.approvalDays > 0,
    raw.competitiveQuotePercent >= 0,
    raw.openCommitmentVisibilityPercent >= 0,
    raw.assetVisibilityPercent >= 0,
  ].filter(Boolean).length;
  return {
    addressableSpend,
    improvementLow: addressableSpend * lowRate,
    improvementHigh: addressableSpend * highRate,
    consolidationReviewSpend,
    negotiationReviewSpend,
    approvalDaysRecoverableRange: [
      Math.max(0, Math.floor(raw.approvalDays * 0.1)),
      Math.max(0, Math.ceil(raw.approvalDays * 0.3)),
    ],
    commitmentVisibilityGap: annualSpend * commitmentGap,
    auditReadinessOpportunity:
      raw.auditPreparationDays > 10 ? "Material evidence-retrieval effort to review" :
      raw.auditPreparationDays > 3 ? "Moderate evidence-retrieval effort to review" :
      "Limited evidence-retrieval effort reported",
    confidence: confidencePoints >= 6 ? "High" : confidencePoints >= 4 ? "Medium" : "Low",
    methodology: [
      "Review spend is calculated only from values and percentages entered in this assessment.",
      "The improvement range applies the user's own conservative low and high assumptions to addressable review spend.",
      "Approval-time range is a scenario of 10%–30% of the entered cycle, not an industry benchmark or guaranteed result.",
      "Commitment visibility gap is exposure requiring review; it is not savings or cash released.",
    ],
    assumptions: [
      "Recurring spend may contain requirements suitable for consolidation review, but suitability requires transaction evidence.",
      "Spend without competitive quotations may merit negotiation review, but policy and market conditions still govern.",
      "No supplier, industry or peer benchmark is used.",
      "All outputs are indicative and do not guarantee savings, time recovery or audit readiness.",
    ],
    actions: [
      "Review recurring demand by specification, location and required date.",
      "Inspect high-value purchases without comparable quotation evidence.",
      "Map open commitments and evidence gaps before changing policy or budgets.",
    ],
  };
}

export function valueStateLabel(state: ValueState) {
  return state === "in_progress" ? "In progress" :
    state === "finance_verified" ? "Finance verified" :
    state.charAt(0).toUpperCase() + state.slice(1);
}
